import { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { api, imgUrl, API } from "@/lib/api";
import { toast } from "sonner";

const F = "block text-[10px] tracking-widest text-neutral-500 mb-1";
const I = "w-full bg-transparent border border-[color:var(--border)] p-2.5 text-sm focus:border-white outline-none";

const STATUSES = ["DRAFT","COMING_SOON","AVAILABLE","SOLD_OUT","ARCHIVED"];
const METHODS = ["DTF","DTG","SCREEN_PRINT","EMBROIDERY","OTHER"];
const TYPES = ["READY_STOCK","MADE_TO_ORDER","PRE_ORDER"];

export default function AdminProductForm() {
  const { id } = useParams();
  const isNew = !id || id === "new";
  const nav = useNavigate();
  const [cats, setCats] = useState([]);
  const [f, setF] = useState({
    article_code: "", name: "", category_id: "", description: "", price: 0, sale_price: "",
    front_image: "", back_image: "", gallery: [], colors: [], sizes: [], size_stock: {},
    status: "DRAFT", production_method: "DTF", production_type: "MADE_TO_ORDER",
    inventory_qty: "", new_drop: false, featured: false, release_date: "",
  });

  useEffect(() => {
    api.get("/categories", { params: { all: true } }).then((r) => setCats(r.data));
    if (!isNew) api.get(`/products/${id}`).then((r) => setF({
      ...r.data,
      gallery: r.data.gallery || [], colors: r.data.colors || [], sizes: r.data.sizes || [],
      size_stock: r.data.size_stock || {},
      sale_price: r.data.sale_price || "",
      inventory_qty: r.data.inventory_qty ?? "",
      production_type: r.data.production_type || "MADE_TO_ORDER",
    }));
  }, [id]);

  const upd = (k) => (e) => setF({ ...f, [k]: e.target.type === "checkbox" ? e.target.checked : e.target.value });

  const upload = async (e, target) => {
    const file = e.target.files[0]; if (!file) return;
    const fd = new FormData(); fd.append("file", file);
    const token = localStorage.getItem("admin_token");
    const r = await fetch(`${API}/images`, { method: "POST", body: fd, headers: { Authorization: `Bearer ${token}` } });
    const d = await r.json();
    if (!r.ok) return toast.error(d.detail || "Upload gagal");
    if (target === "gallery") setF((prev) => ({ ...prev, gallery: [...prev.gallery, d.key] }));
    else setF((prev) => ({ ...prev, [target]: d.key }));
  };

  const submit = async (e) => {
    e.preventDefault();
    const body = {
      ...f,
      price: Number(f.price),
      sale_price: f.sale_price === "" ? null : Number(f.sale_price),
      inventory_qty: f.inventory_qty === "" ? null : Number(f.inventory_qty),
    };
    try {
      if (isNew) await api.post("/products", body); else await api.put(`/products/${id}`, body);
      toast.success("Tersimpan"); nav("/admin/products");
    } catch (err) { toast.error(err?.response?.data?.detail || "Gagal"); }
  };

  const csv = (v) => (Array.isArray(v) ? v.join(", ") : "");
  const setCsv = (k) => (e) => setF({ ...f, [k]: e.target.value.split(",").map((s) => s.trim()).filter(Boolean) });

  return (
    <form onSubmit={submit} className="max-w-4xl">
      <h1 className="display text-4xl md:text-6xl mb-6">{isNew ? "NEW PRODUCT" : "EDIT PRODUCT"}</h1>
      <div className="grid md:grid-cols-2 gap-4">
        <div><label className={F}>ARTICLE CODE *</label><input data-testid="p-code" required className={I} value={f.article_code} onChange={upd("article_code")} placeholder="TS-002" /></div>
        <div><label className={F}>NAMA *</label><input data-testid="p-name" required className={I} value={f.name} onChange={upd("name")} /></div>
        <div><label className={F}>KATEGORI *</label>
          <select data-testid="p-cat" required className={I} value={f.category_id} onChange={upd("category_id")}>
            <option value="">— pilih —</option>
            {cats.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
          </select>
        </div>
        <div className="grid grid-cols-2 gap-3">
          <div><label className={F}>HARGA (Rp) *</label><input data-testid="p-price" required type="number" min="0" className={I} value={f.price} onChange={upd("price")} /></div>
          <div><label className={F}>SALE PRICE</label><input data-testid="p-sale" type="number" min="0" className={I} value={f.sale_price} onChange={upd("sale_price")} placeholder="opsional" /></div>
        </div>
        <div className="md:col-span-2"><label className={F}>DESKRIPSI</label><textarea rows={4} className={I} value={f.description} onChange={upd("description")} /></div>

        <div><label className={F}>WARNA (dipisah koma)</label><input data-testid="p-colors" className={I} value={csv(f.colors)} onChange={setCsv("colors")} placeholder="Black, White" /></div>
        <div><label className={F}>UKURAN (dipisah koma)</label><input data-testid="p-sizes" className={I} value={csv(f.sizes)} onChange={setCsv("sizes")} placeholder="S, M, L, XL, XXL" /></div>

        <div><label className={F}>STATUS</label><select data-testid="p-status" className={I} value={f.status} onChange={upd("status")}>{STATUSES.map((s) => <option key={s}>{s}</option>)}</select></div>
        <div className="grid grid-cols-2 gap-3">
          <div><label className={F}>TIPE PRODUKSI</label><select data-testid="p-type" className={I} value={f.production_type} onChange={upd("production_type")}>{TYPES.map((s) => <option key={s}>{s}</option>)}</select></div>
          <div><label className={F}>METODE CETAK</label><select data-testid="p-method" className={I} value={f.production_method} onChange={upd("production_method")}>{METHODS.map((s) => <option key={s}>{s}</option>)}</select></div>
        </div>

        {f.production_type === "READY_STOCK" && f.sizes.length > 0 && (
          <div className="md:col-span-2 border border-[color:var(--border)] p-4">
            <label className={F}>STOK PER UKURAN (READY_STOCK)</label>
            <div className="grid grid-cols-3 md:grid-cols-7 gap-2">
              {f.sizes.map((s) => (
                <div key={s}>
                  <div className="text-[10px] text-neutral-500 mb-1">{s}</div>
                  <input data-testid={`p-stock-${s}`} type="number" min="0" className={I}
                    value={(f.size_stock || {})[s] ?? ""}
                    onChange={(e) => setF({ ...f, size_stock: { ...(f.size_stock || {}), [s]: e.target.value === "" ? 0 : Number(e.target.value) } })} />
                </div>
              ))}
            </div>
            <p className="text-[10px] text-neutral-500 mt-2">Stok akan dikurangi otomatis saat order dibayar. Setiap perubahan tercatat di INVENTORY.</p>
          </div>
        )}
        {f.production_type === "MADE_TO_ORDER" && (
          <div className="md:col-span-2 text-[11px] text-neutral-400 border border-[color:var(--border)] px-4 py-3">MADE TO ORDER — stok fisik tidak dilacak. Setiap order langsung tercatat sebagai reservasi produksi.</div>
        )}

        <div><label className={F}>INVENTORY LEGACY (opsional)</label><input data-testid="p-inv" type="number" min="0" className={I} value={f.inventory_qty} onChange={upd("inventory_qty")} placeholder="opsional" /></div>
        <div><label className={F}>RELEASE DATE</label><input type="date" className={I} value={(f.release_date || "").slice(0,10)} onChange={upd("release_date")} /></div>

        <div className="flex items-center gap-4 md:col-span-2">
          <label className="text-xs"><input data-testid="p-new-drop" type="checkbox" checked={f.new_drop} onChange={upd("new_drop")} /> NEW DROP</label>
          <label className="text-xs"><input data-testid="p-featured" type="checkbox" checked={f.featured} onChange={upd("featured")} /> FEATURED</label>
        </div>

        <div>
          <label className={F}>FRONT IMAGE</label>
          {f.front_image && <img src={imgUrl(f.front_image)} className="w-24 h-32 object-cover mb-2" alt="" />}
          <input data-testid="p-front" type="file" accept="image/*" onChange={(e) => upload(e, "front_image")} />
        </div>
        <div>
          <label className={F}>BACK IMAGE</label>
          {f.back_image && <img src={imgUrl(f.back_image)} className="w-24 h-32 object-cover mb-2" alt="" />}
          <input data-testid="p-back" type="file" accept="image/*" onChange={(e) => upload(e, "back_image")} />
        </div>
        <div className="md:col-span-2">
          <label className={F}>GALLERY</label>
          <div className="flex gap-2 flex-wrap mb-2">
            {f.gallery.map((k, i) => (
              <div key={i} className="relative"><img src={imgUrl(k)} className="w-20 h-24 object-cover" alt="" />
                <button type="button" onClick={() => setF({ ...f, gallery: f.gallery.filter((_, j) => j !== i) })} className="absolute top-0 right-0 bg-black/70 text-white text-[10px] px-1">×</button>
              </div>
            ))}
          </div>
          <input type="file" accept="image/*" onChange={(e) => upload(e, "gallery")} />
        </div>
      </div>
      <div className="mt-8 flex gap-3">
        <button data-testid="p-save" className="bg-white text-black px-8 py-3 text-xs tracking-widest">SIMPAN</button>
        <button type="button" onClick={() => nav("/admin/products")} className="border border-white px-8 py-3 text-xs tracking-widest">BATAL</button>
      </div>
    </form>
  );
}
